import { Injectable } from "@angular/core";
import { environment } from "../../../../environments/environment";
import { HttpClient, HttpParams } from "@angular/common/http";
import { Observable, of, Subject, forkJoin, throwError } from "rxjs";
import * as _ from 'lodash';
import { map, mergeMap, catchError } from "rxjs/operators";
import { FileService, CacheService, ToastrService } from '@mapfre-tech/amap3';
import { normalizeUser } from '../user/user.model';
import { TranslateService } from '@ngx-translate/core';

@Injectable({
   providedIn: "root"
})
export class RecommendationService {

   className = 'RecommendationService';

   constructor(private http: HttpClient, private fileService: FileService, private cacheService: CacheService,
      private toastService: ToastrService, private translateService: TranslateService) { }

   //!NO se usa
   getRecommendationByFactObserverId(factObserverId, jobId): Observable<any[]> {
      return this.http.get<any[]>(environment.base_URL + "/recommendation", { params: { factObserverId: factObserverId, jobId } });
   }

   getDashboardInfo() {
      return this.http.get<any[]>(environment.base_URL + '/dashboard-info');
   }

   getRecommendationById(id, audiweb): Observable<any[]> {
      const end = audiweb ? '?audiweb=true' : '';
      return this.http.get<any[]>(environment.base_URL_GAIA + `/recommendation/${id}` + end);
   }

   getImpactTypesRecommendation(cache = true, cacheKey = "recImpactTypes"): Observable<any[]> {
      if (this.cacheService.canUseCache(cacheKey) && cache) {
         return this.cacheService.getCacheData(cacheKey);
       } else {
         const observable = this.http.get<any[]>(environment.base_URL + '/recommendation/impact-types').pipe(map((res) => {
         this.cacheService.updateCacheData(res, cacheKey)
         return res;
         }))
         this.cacheService.updateCacheReq(cacheKey);
         return observable;
       }
   }

   getProbabilityTypesRecommendation(cache = true, cacheKey = "recProbabilityTypes"): Observable<any[]> {
      if (this.cacheService.canUseCache(cacheKey) && cache) {
         return this.cacheService.getCacheData(cacheKey);
       } else {
         const observable = this.http.get<any[]>(environment.base_URL + '/recommendation/probability-types').pipe(map((res) => {
         this.cacheService.updateCacheData(res, cacheKey)
         return res;
         }))
         this.cacheService.updateCacheReq(cacheKey);
         return observable;
       }
   }

   exportRecommendation(key, params) {
      _.set(params, "key", key + '/data.json');

      // Call aws gateway to generate pre signed url
      return this.http.get<any[]>(environment.base_URL + "/exported-url", { params })
         .pipe(mergeMap((res: any) => {

            // Remove key paramenter and add url parameter
            _.unset(params, "key")
            _.set(params, "url", res)

            // Call Mapgre Gateway to generate excel with exoprted jobs
            return this.http.get<any[]>(environment.base_URL + "/exported-recommendations", { params })
               .pipe(mergeMap((res: any) => {

                  // Remove url paramenter and add key parameter
                  const paramsExcel = {}
                  _.set(paramsExcel, "keydata", key + '/data.json');
                  _.set(paramsExcel, "keyexcel", key + '/export.xlsx');

                  return this.http.get<any[]>(environment.base_URL + "/create-excel", { params: paramsExcel })
                     .pipe(mergeMap((res3: any) => {

                        // Remove url paramenter and add key parameter
                        const paramsDownload = {}
                        _.set(paramsDownload, "onlyDownload", true)

                        const body = {
                              key: key + '/export.xlsx'
                            }

                        // Download excel
                        return this.http.post(environment.base_URL + `/download`, body, { params: paramsDownload })
                           .pipe(mergeMap((res: any) => {

                              // Download file
                              const options = {
                                 responseType: "blob" as 'json'
                              }

                              // Download file form S3
                              return this.http.get(res, options);

                           }), map(res => {

                              // Delete file when downloaded
                              this.delete(key).subscribe();
                              return res;

                           }), catchError(err => {
                              this.toastService.add({
                                 severity: "error",
                                 summary: this.translateService.instant("TOAST.GENERAL.ERROR_FILE_TITLE"),
                                 detail: this.translateService.instant("TOAST.GENERAL.ERROR_FILE_DETAIL"), life: 5000
                              });
                              return throwError(err);
                           }))
                     }))
               }))

         }), map(res => {
            return res;
         }), catchError(err => {
            this.toastService.add({
               severity: "error",
               summary: this.translateService.instant("TOAST.GENERAL.ERROR_FILE_TITLE"),
               detail: this.translateService.instant("TOAST.GENERAL.ERROR_FILE_DETAIL"), life: 5000
            });
            return throwError(err);
         }))

   }

   delete(key): any {
      const params = {
         key
      }
      return this.http.post(environment.base_URL + "/delete", { params }).pipe(catchError(err => {
         this.toastService.add({
            severity: "error",
            summary: this.translateService.instant("TOAST.GENERAL.ERROR_FILE_TITLE"),
            detail: this.translateService.instant("TOAST.GENERAL.ERROR_FILE_DETAIL"), life: 5000
         });
         return throwError(err);
      }));
   }

   comment(comment) {
      return this.http.post(environment.base_URL + `/recommendation/comment`, comment);
   }

   //NO SE USA
   // getCommentsRecommendationById(id) {
   //    let params = {
   //       recommendationIds: id.join(','),
   //       audiweb: 'true'
   //    };
   //    return this.http.get(environment.base_URL + `/recommendations/comments`, { params });
   // }

   getAllRecommendations(select: any = false, cache=true, cacheKey = "allRecommendations"): Observable<any[]> {
      if (this.cacheService.canUseCache(cacheKey, [select ? 'lite' : 'full']) && cache) {
         return this.cacheService.getCacheData(cacheKey, [select ? 'lite' : 'full']);
      } else {
         const observable = this.http.get<any[]>(environment.base_URL + "/recommendation", { params: { select: select } }).pipe(map((res) => {
            if (cache) {
               this.cacheService.updateCacheData(res, cacheKey, [select ? 'lite' : 'full'])
            }
            return res;
         }))
         if (cache) {
            this.cacheService.updateCacheReq(cacheKey, [select ? 'lite' : 'full']);
         }
         return observable;
      }
   }

   removeRecommendationByProgramJobId(programJobId, code): Observable<any[]> {
      return this.http.delete<any[]>(environment.base_URL + "/recommendation/" + programJobId + "/code/" + code);
   }

   createRecommendation(recommendation): Observable<any[]> {
      return this.http.post<any[]>(environment.base_URL + "/recommendation", recommendation);
   }

   updateRecommendation(recommendation): Observable<any[]> {
      return this.http.put<any[]>(environment.base_URL + "/recommendation", recommendation);
   }

   getTypeCritiqueRecommendation(cache = true, cacheKey = 'typeCritique'): Observable<any[]> {

      if (this.cacheService.canUseCache(cacheKey) && cache) {
         return this.cacheService.getCacheData(cacheKey);
      } else {
         const observable = this.http.get<any[]>(environment.base_URL + "/types-critique-recommendation").pipe(map((res) => {
            this.cacheService.updateCacheData(res, cacheKey)
            return res;
         }))
         this.cacheService.updateCacheReq(cacheKey);
         return observable;
      }

   }

   getTypeStatusRecommendation(cache = true, cacheKey = "typeStatus"): Observable<any[]> {

      if (this.cacheService.canUseCache(cacheKey) && cache) {
         return this.cacheService.getCacheData(cacheKey);
      } else {
         const observable = this.http.get<any[]>(environment.base_URL + "/types-status-recommendation").pipe(map((res) => {
            this.cacheService.updateCacheData(res, cacheKey)
            return res;
         }))
         this.cacheService.updateCacheReq(cacheKey);
         return observable;
      }
   }

   getTypeStatusActionPlan(cache = true, cacheKey = "typeStatusActionPlan") {
      if (this.cacheService.canUseCache(cacheKey) && cache) {
         return this.cacheService.getCacheData(cacheKey);
      } else {
         const observable = this.http.get<any[]>(environment.base_URL + "/types-status-action-plan").pipe(map((res) => {
            this.cacheService.updateCacheData(res, cacheKey)
            return res;
         }))
         this.cacheService.updateCacheReq(cacheKey);
         return observable;
      }
   }

   getTypeRiskRecommendation(cache = true, cacheKey = "typeRisk"): Observable<any[]> {

      if (this.cacheService.canUseCache(cacheKey) && cache) {
         return this.cacheService.getCacheData(cacheKey);
      } else {
         const observable = this.http.get<any[]>(environment.base_URL + "/types-risk").pipe(map((res) => {
            this.cacheService.updateCacheData(res, cacheKey)
            return res;
         }))
         this.cacheService.updateCacheReq(cacheKey);
         return observable;
      }

   }

   deleteRecommendation(recommendationId, recommendationCode): Observable<any[]> {
      return this.http.delete<any[]>(environment.base_URL + "/recommendation/" + recommendationId + "/code/" + recommendationCode);
   }

   getAllByJobId(jobId, cache?, cacheKey = "allByJobId") {

      if (this.cacheService.canUseCache(cacheKey, [jobId]) && cache) {
         return this.cacheService.getCacheData(cacheKey, [jobId]);
      } else {
         const observable = this.http.get<any[]>(environment.base_URL + "/recommendation", { params: { jobId: jobId } }).pipe(map((res) => {
            if (cache) {
               this.cacheService.updateCacheData(res, cacheKey, [jobId])
            }
            _.forEach(res, (r) => {
               if (_.get(r, 'actionPlan.userResponsible.idUserPk')) {
                  r.actionPlan.userResponsible = normalizeUser(r.actionPlan.userResponsible);
               }
            })
            return res;
         }))
         if (cache) {
            this.cacheService.updateCacheReq(cacheKey, [jobId]);
         }
         return observable;
      }


   }

   updateActionPlan(actionPlan): Observable<any[]> {
      return this.http.put<any[]>(environment.base_URL + "/recommendation/action-plan", actionPlan);
      //return of([1989,1999,2009]);
   }

   createActionPlan(actionPlan): Observable<any[]> {
      return this.http.post<any[]>(environment.base_URL + "/recommendation/action-plan", actionPlan);
      //return of([1948]);
   }

   /***
    *  Recomendation Status
    */

   newTypeStatusRecommendation(status) {
      return this.http.post(environment.base_URL + "/types-status-recommendation", status)
   }

   updateTypeStatusRecommendation(status, id) {
      return this.http.put(environment.base_URL + `/types-status-recommendation/${id}`, status)
   }

   /***
    *  Recomendations Critique
    */


   newTypeCritiqueRecommendation(critique) {
      return this.http.post(environment.base_URL + "/types-critique-recommendation", critique)
   }

   updateTypeCritiqueRecommendation(critique, id) {
      return this.http.put(environment.base_URL + `/types-critique-recommendation/${id}`, critique)
   }

   export(recommendation, jobId): Observable<any[]> {
      const params = {
         jobId
      }
      const options = {
         params,
         responseType: "blob" as 'json'
      }
      return this.http.post<any[]>(environment.base_URL_GAIA + "/recommendation/" + recommendation.idtSqn + "/export", recommendation, options);
   }

   createRecommendationWithFiles(recommendation): Observable<any> {
      // PRE FILES INI
      const filesToUpload = _.remove(_.get(recommendation, '[0].files'), (f) => f instanceof File);
      // PRE FILES END
      //CHECK IF THERE ARE NEW FILES
      if (!_.isEmpty(filesToUpload)) {
         return this.createRecommendation(recommendation).pipe(mergeMap((res: any) => {
            const observables = _.map(filesToUpload, (f) => this.fileService.upload(f, 'recommendation', _.get(res, '[0]'), undefined, true));
            return forkJoin(observables).pipe(map(resultFiles => {
               return res;
            }));
         }))
      }
      else {
         return this.createRecommendation(recommendation);
      }
   }

   updateRecommendationWithFiles(recommendation): Observable<any> {
      // PRE FILES INI
      const filesToUpload = _.remove(_.get(recommendation, '[0].files'), (f) => f instanceof File);

      // PRE FILES END
      //CHECK IF THERE ARE NEW FILES
      if (!_.isEmpty(filesToUpload)) {
         return this.updateRecommendation(recommendation).pipe(mergeMap((res: any) => {
            const observables = _.map(filesToUpload, (f) => this.fileService.upload(f, 'recommendation', _.get(recommendation, '[0].idtSqn'), undefined, true));
            return forkJoin(observables).pipe(map(resultFiles => {
               return res;
            }));
         }))
      }
      else {
         return this.updateRecommendation(recommendation);
      }
   }

   getFilteredRecommendations(params): Observable<any[]> {
      return this.http.get<any[]>(environment.base_URL + "/filtered-recommendations", { params });
   }

   getRecommendationByUserId(user): Observable<any[]> {
      return this.http.get<any[]>(environment.base_URL + `/recommendation`, { params: { user: user } });
   }

   /**
    * Delete Cache
    */

   deleteCache() {

      const memoryCache = ["typeCritique", "typeRisk", "allByJobId", "typeStatus", "allRecommendations", "typeStatusActionPlan"];

      _.forEach(memoryCache, item => {
         this.cacheService.deleteCache(item);
      })

   }


}
